import { useMemo } from 'react'
import {
  getName,
  isCompanyId,
  isDefined,
  isNotEmpty,
  isRoleId,
  isTeamId,
  isUserId,
  type CompanyId,
  type PermissionEntityId,
  type PermissionListItem,
  type TeamId,
  type PermissionType,
  type TinyTeam,
  type UserData,
} from '@terros/common'
import { useCompany, useProfile, useRoles, useTeam, useTeams, useUserTeamMemberships } from '@front/model'
import { type PermGraph } from './types'
import { buildPermGraph } from './buildPermGraph'

type Result = {
  currentUserId: PermissionEntityId | undefined
  error?: string
  graph?: PermGraph
  isLoading: boolean
}

export function usePermGraph({
  enabled,
  entityId,
  info,
  permissionType,
}: {
  enabled: boolean
  entityId: PermissionEntityId
  info: PermissionListItem
  permissionType: PermissionType
}): Result {
  const viewer = useProfile()
  const targetUserId = isUserId(entityId) ? entityId : undefined
  const targetTeamId = isTeamId(entityId) ? entityId : undefined

  const profile = useProfile(targetUserId, enabled && isDefined(targetUserId))
  const targetUser = isDefined(targetUserId) ? profile.user : undefined

  const teamGet = useTeam(targetTeamId, enabled && isDefined(targetTeamId))
  const targetTeam = isDefined(targetTeamId) ? teamGet.data?.team : undefined
  const teamList = useTeams({ enabled: enabled && isNotEmpty(targetTeam?.parentTeamIds ?? []) })

  const companyId = toTargetCompanyId({
    permissionInfo: info,
    targetUserCompanyId: isDefined(targetUserId) ? profile.company?.companyId : undefined,
    teamMemberCompanyId: isDefined(targetTeamId) ? toTeamMemberCompanyId(teamGet.data?.members) : undefined,
    viewerCompanyId: viewer.company?.companyId,
  })
  const company = useCompany({ companyId }, enabled)
  const companyName = company.data?.name ?? viewer.company?.name

  const memberOfTeams = useMemo(() => targetUser?.teams?.memberOf ?? [], [targetUser])
  const directTeamIds = useMemo(() => targetUser?.teams?.directMemberOf.map((item) => item.teamId) ?? [], [targetUser])
  const memberships = useUserTeamMemberships(enabled ? targetUserId : undefined, directTeamIds)
  const needsRoles = isDefined(targetUserId) || isRoleId(entityId)
  const roles = useRoles({ companyId, enabled: enabled && isDefined(companyId) && needsRoles })
  const uplineTeams = useMemo(() => toUplineTeams(targetTeam, teamList.data ?? []), [targetTeam, teamList.data])

  const isLoading =
    profile.isLoading ||
    teamGet.isLoading ||
    teamList.isLoading ||
    memberships.isLoading ||
    roles.isLoading ||
    company.isLoading
  const targetLabel = toTargetLabel({
    companyName,
    entityId,
    roleName: roles.data?.find((role) => role.roleId === entityId)?.name,
    teamName: targetTeam?.name,
    userName: targetUser ? getName(targetUser) : undefined,
  })

  const graph = useMemo<{ error?: string; graph?: PermGraph } | undefined>(() => {
    if (!enabled || isLoading || !companyId) return
    try {
      return {
        graph: buildPermGraph({
          companyId,
          companyName: companyName ?? 'Company',
          directRoles: targetUser?.directRoles ?? [],
          directTeamIds,
          memberships: memberships.memberships,
          permissionInfo: info,
          permissionType,
          roles: roles.data ?? [],
          targetId: entityId,
          targetLabel,
          teams: isDefined(targetTeamId) ? uplineTeams : memberOfTeams,
        }),
      }
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Unable to build the permission graph' }
    }
  }, [
    companyId,
    companyName,
    directTeamIds,
    enabled,
    entityId,
    info,
    isLoading,
    memberOfTeams,
    memberships.memberships,
    permissionType,
    roles.data,
    targetLabel,
    targetTeamId,
    targetUser,
    uplineTeams,
  ])

  return {
    currentUserId: viewer.user?.userId,
    error:
      graph?.error ??
      profile.error ??
      teamGet.error ??
      teamList.error ??
      memberships.error ??
      roles.error ??
      company.error,
    graph: graph?.graph,
    isLoading,
  }
}

export type PermGraphTeamMember = {
  user: Pick<UserData, 'companyId'>
}

export const toTeamMemberCompanyId = (members: PermGraphTeamMember[] | undefined): CompanyId | undefined =>
  members?.find((member) => isDefined(member.user.companyId))?.user.companyId

export function toTargetCompanyId({
  permissionInfo,
  targetUserCompanyId,
  teamMemberCompanyId,
  viewerCompanyId,
}: {
  permissionInfo: PermissionListItem
  targetUserCompanyId: CompanyId | undefined
  teamMemberCompanyId: CompanyId | undefined
  viewerCompanyId: CompanyId | undefined
}): CompanyId | undefined {
  if (isDefined(targetUserCompanyId)) return targetUserCompanyId
  const contributing = [
    ...permissionInfo.allScopes.map((scope) => scope.source),
    ...(permissionInfo.allOverrides?.map((override) => override.entityId) ?? []),
  ].find(isCompanyId)
  return contributing ?? teamMemberCompanyId ?? viewerCompanyId
}

export function toUplineTeams(target: TinyTeam | undefined, ancestors: TinyTeam[]): TinyTeam[] {
  if (!target) return []

  const byId = new Map(ancestors.map((team) => [team.teamId, team]))
  const chain = [target]
  const seen = new Set<TeamId>([target.teamId])

  const walk = (parentId: TeamId | undefined): void => {
    if (!parentId || seen.has(parentId)) return
    const parent = byId.get(parentId)
    if (!parent) return
    seen.add(parentId)
    chain.push(parent)
    walk(parent.parentId)
  }

  walk(target.parentId)
  return chain
}

function toTargetLabel({
  companyName,
  entityId,
  roleName,
  teamName,
  userName,
}: {
  companyName: string | undefined
  entityId: PermissionEntityId
  roleName: string | undefined
  teamName: string | undefined
  userName: string | undefined
}): string {
  if (isUserId(entityId)) return userName ?? entityId
  if (isTeamId(entityId)) return teamName ?? entityId
  if (isRoleId(entityId)) return roleName ?? entityId
  return companyName ?? entityId
}
